/**
 * Thin wrapper over the Umami tracker.
 *
 * Everything here must be safe to call before (or without) the script loading:
 * ad-blockers, the self-hosted instance being down, or SSR all leave
 * `window.umami` undefined, and analytics must never break a user flow.
 */

type EventData = Record<string, string | number | boolean>;

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: EventData) => void;
      identify?: (data: EventData) => void;
    };
  }
}

/**
 * Funnel events, declared in one place so names can't drift between call
 * sites — Umami groups by exact string and a typo silently creates a second,
 * near-empty event.
 */
export const EVENTS = {
  /** Signup form displayed. `from` carries the page that sent the visitor. */
  signupStarted: "signup_started",
  signupCompleted: "signup_completed",
  /** Guest mode entered from a public page — the top of the SEO funnel. */
  guestStarted: "guest_started",
  /** Onboarding answered through: carries template, level and climate zone. */
  onboardingCompleted: "onboarding_completed",
  /** Onboarding abandoned for a blank plan — `step` says where. */
  onboardingSkipped: "onboarding_skipped",
  /** A garden was created — `mode` distinguishes guest drafts from accounts. */
  gardenCreated: "garden_created",
  /** A guest plan was carried into a freshly created account. */
  guestGardenMigrated: "guest_garden_migrated",
  /** First plant placed in an editor session: the activation moment. */
  plantPlaced: "plant_placed",
  /** Garden switched to public, i.e. added to the gallery. */
  gardenPublished: "garden_published",
  harvestLogged: "harvest_logged",
  photoUploaded: "photo_uploaded",
  /** A public garden was duplicated from the gallery. */
  planForked: "plan_forked",
  /**
   * Someone told us on /tarifs which paid feature they would want. `feature`
   * carries which one — this is the only measure of purchase intent we have
   * while checkout stays closed.
   */
  pricingIntent: "pricing_intent",
} as const;

export function track(event: string, data?: EventData): void {
  if (typeof window === "undefined") return;
  try {
    window.umami?.track(event, data);
  } catch {
    // Never let a tracking failure surface to the user.
  }
}

/**
 * Referrer reduced to something groupable in Umami: an internal path, the bare
 * external host, or "direct". Full URLs would explode the cardinality of the
 * property and make the report useless.
 */
export function referrerLabel(): string {
  if (typeof document === "undefined") return "unknown";
  const raw = document.referrer;
  if (!raw) return "direct";
  try {
    const url = new URL(raw);
    if (url.host === window.location.host) return url.pathname;
    return url.host;
  } catch {
    return "unknown";
  }
}
